import {
	DealstackAiReasonCode,
	DealstackAiTransactionListSort,
	type CostViewRes,
	type DealstackAiTransactionFieldKey,
	type DealstackAiTransactionQuickFilter,
	type StagedTransactionRes,
	type TransactionQuickFilterCountsRes,
	type TransactionsPageRes,
} from '@dealstack/shared/lib/backendClient';
import { REASON_CODE_METADATA } from '@dealstack/shared/lib/dealstackAi/reasonCodeMetadata';
import {
	DATE_FORMATS,
	formatBEDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date';
import type { TransactionListQuery } from '@dealstack/frontend/app/services/dealstackAi/dealstackAi.types';
import type {
	CachedTransactionPage,
	TransactionPageView,
} from '../../hooks/useTransactionPages.types';
import { isOneOf, oneOfOr } from '../../workspaceUrlState.utils';
import type { CellAlign, CellTrack } from '../DataGrid/DataGrid.types';
import { QUICK_FILTER_KEYS, REASON_KEYS } from './transactionCopy.utils';
import {
	CHEVRON_COLUMN_WIDTH,
	COLUMN_MIN_WIDTH,
	QUICK_FILTER_ORDER,
	SELECT_COLUMN_WIDTH,
	STATUS_COLUMN_WIDTH,
	TRANSACTIONS_PAGE_SIZE,
} from './TransactionsPanel.constants';
import type {
	AskedPageRetry,
	CostSummary,
	FailedPageRetryOptions,
	PageRetryState,
	QuickFilterKeyOption,
	ReasonFilterOption,
	TransactionColumnKey,
	JoinedTransactionPages,
	RowWindow,
	TransactionListItem,
	TransactionsListNarrowing,
	TransactionsScopeFilter,
} from './TransactionsPanel.types';

/**
 * The panel's list query, without the page. Prefetches build it the same way so they hit the same cache entry: the
 * default sort stays out of it.
 */
export const transactionsListFilter = (
	filter: TransactionsScopeFilter,
	quickFilter: DealstackAiTransactionQuickFilter,
	search = '',
	{ reasonCode, sort }: TransactionsListNarrowing = {}
): TransactionListQuery => ({
	...filter,
	quickFilter,
	search: search.trim() || undefined,
	reasonCode,
	sort: sort === DealstackAiTransactionListSort.CHECKS ? sort : undefined,
	pageSize: TRANSACTIONS_PAGE_SIZE,
});

/**
 * The cached pages as one list. Page 1 carries the freshest count: a later page starting at or past it is left out,
 * and a page not showing rows (a placeholder, or failed) holds its rows' places, as many as that count leaves it. A
 * place keeps its key whether it shows as a placeholder or failed, so it keeps its element (and focus) across a retry.
 * A row a write moved between two page fetches is kept once, where it first appeared.
 */
export const joinTransactionPages = (
	pages: readonly CachedTransactionPage[]
): JoinedTransactionPages => {
	const filteredCount = pages[0]?.data.filteredCount ?? 0;
	const pageSize = pages[0]?.data.pageSize ?? TRANSACTIONS_PAGE_SIZE;
	const held = pages.filter((_, index) => index === 0 || index * pageSize < filteredCount);
	const placeCount = (index: number) => Math.min(pageSize, filteredCount - index * pageSize);
	const seen = new Set<string>();
	const items: TransactionListItem[] = [];
	const rowPages: number[] = [];
	held.forEach(({ data, view }, index) => {
		const page = index + 1;
		if (view !== 'rows') {
			for (let slot = 0; slot < placeCount(index); slot += 1) {
				items.push({ kind: view, key: `place-${page}-${slot}`, page });
				rowPages.push(page);
			}
			return;
		}
		for (const row of data.rows) {
			if (!seen.has(row.id)) {
				seen.add(row.id);
				items.push({ kind: 'row', row });
				rowPages.push(page);
			}
		}
	});
	const last = held.at(-1);
	const lastCount = last?.view === 'rows' ? last.data.rows.length : placeCount(held.length - 1);
	const pagesShowing = (view: TransactionPageView) =>
		held.flatMap((entry, index) => (entry.view === view ? [index + 1] : []));
	return {
		firstPage: pages[0]?.data,
		items,
		rowPages,
		pageCount: held.length,
		placeholderPages: pagesShowing('placeholder'),
		failedPages: pagesShowing('failed'),
		morePages: last !== undefined && lastCount > 0 && held.length * pageSize < filteredCount,
	};
};

/** Where the row `rowId` sits in the list, or -1 (none named, or not in the list). */
export const indexOfRow = (items: readonly TransactionListItem[], rowId: string | null): number =>
	rowId === null ? -1 : items.findIndex((item) => item.kind === 'row' && item.row.id === rowId);

/**
 * Where each failed page says so: its topmost place from `firstWhole` (the first row whose top edge is in view), so
 * the message and its "Try again" stay whole on screen wherever the reviewer is inside the page. The place that holds
 * focus (`focusedIndex`) keeps its page's message, so focus is not lost when that place's top edge is cut.
 */
export const failedMessageIndexes = (
	items: readonly TransactionListItem[],
	firstWhole: number,
	focusedIndex = -1
): Map<number, number> => {
	const at = new Map<number, number>();
	const focused = items[focusedIndex];
	if (focused?.kind === 'failed') {
		at.set(focused.page, focusedIndex);
	}
	for (let index = Math.max(0, firstWhole); index < items.length; index += 1) {
		const item = items[index];
		if (item?.kind === 'failed' && !at.has(item.page)) {
			at.set(item.page, index);
		}
	}
	return at;
};

/** Where the asked page retry stands, read from the pages not showing rows. */
export const pageRetryState = (
	asked: AskedPageRetry | null,
	scope: string,
	{ failedPages, placeholderPages }: FailedPageRetryOptions['pages']
): PageRetryState => {
	if (asked === null || asked.scope !== scope) {
		return 'idle';
	}
	if (failedPages.includes(asked.page)) {
		return 'failed';
	}
	return placeholderPages.includes(asked.page) ? 'loading' : 'loaded';
};

/** A list item's key: its row's id, or the place's. */
export const itemKey = (item: TransactionListItem): string =>
	item.kind === 'row' ? item.row.id : item.key;

/**
 * The total the growth footer counts to: the server's, or once every page is in, the rows that arrived (rows a write
 * moved between two page fetches can leave fewer), so the footer never offers rows it cannot show.
 */
export const growthTotal = (filteredCount: number, loaded: number, complete: boolean): number =>
	complete ? Math.min(filteredCount, loaded) : filteredCount;

/**
 * The pages to observe: page 1 (the counts), those under the rows on screen and under the rows kept rendered off it
 * (the open and the focused one), and the pages being loaded.
 */
export const observedTransactionPages = (
	rowPages: readonly number[],
	{ first, last, kept }: RowWindow,
	loading: readonly (number | null)[]
): number[] =>
	[
		...new Set([
			1,
			...rowPages.slice(Math.max(0, first), Math.max(0, last) + 1),
			...kept.flatMap((index) => (rowPages[index] === undefined ? [] : [rowPages[index]])),
			...loading.filter((page) => page !== null),
		]),
	].sort((left, right) => left - right);

export const sameRowWindow = (left: RowWindow, right: RowWindow): boolean =>
	left.first === right.first &&
	left.last === right.last &&
	left.kept.join() === right.kept.join();

const REASON_CODES = Object.values(DealstackAiReasonCode);
const SORTS = Object.values(DealstackAiTransactionListSort);

export const parseReasonFilter = (value: string | null): DealstackAiReasonCode | null =>
	isOneOf(value, REASON_CODES) ? value : null;

export const parseSort = (value: string | null): DealstackAiTransactionListSort =>
	oneOfOr(value, SORTS, DealstackAiTransactionListSort.DOCUMENT);

/** A data column's track: it grows evenly above its floor. Status is fixed and, with the chevron, pinned right. */
export const columnTrack = (column: TransactionColumnKey): CellTrack =>
	column === 'status'
		? {
				width: STATUS_COLUMN_WIDTH,
				pinRight: `${CHEVRON_COLUMN_WIDTH}px`,
				shade: true,
			}
		: { min: COLUMN_MIN_WIDTH[column] };

/** The leading selection column's track. */
export const SELECT_TRACK: CellTrack = { width: SELECT_COLUMN_WIDTH };

/** The trailing chevron column's track: pinned at the right edge, beside Status. */
export const CHEVRON_TRACK: CellTrack = { width: CHEVRON_COLUMN_WIDTH, pinRight: '0px' };

/** The table's width before it scrolls sideways: every column at its floor, and never under 920px. */
export const tableMinWidth = (columns: TransactionColumnKey[]): number =>
	Math.max(
		920,
		columns.reduce(
			(width, column) =>
				width + (column === 'status' ? STATUS_COLUMN_WIDTH : COLUMN_MIN_WIDTH[column]),
			SELECT_COLUMN_WIDTH + CHEVRON_COLUMN_WIDTH
		)
	);

export const columnAlign = (column: TransactionColumnKey): CellAlign =>
	column === 'units' || column === 'cost' ? 'end' : 'start';

export const quickFilterKeyOptions = (
	counts: TransactionQuickFilterCountsRes | undefined
): QuickFilterKeyOption[] =>
	QUICK_FILTER_ORDER.map((value) => ({
		value,
		labelKey: QUICK_FILTER_KEYS[value],
		count: counts?.[value] ?? 0,
	}));

const SEVERITY_RANK = { hard: 0, blocking: 1, soft: 2, info: 3 } as const;

/** Reason codes with at least one row, most severe first, then in the contract's order. */
export const reasonFilterOptions = (
	reasonCounts: TransactionsPageRes['reasonCounts'] | undefined
): ReasonFilterOption[] =>
	REASON_CODES.map((code) => ({
		code,
		labelKey: REASON_KEYS[code],
		count: reasonCounts?.[code] ?? 0,
		severity: REASON_CODE_METADATA[code].severity,
	}))
		.filter(({ count }) => count > 0)
		.sort(
			(left, right) =>
				SEVERITY_RANK[left.severity] - SEVERITY_RANK[right.severity] ||
				REASON_CODES.indexOf(left.code) - REASON_CODES.indexOf(right.code)
		);

const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;

/** Rounds a fraction to `digits` places, half away from zero, on the digits themselves. */
const roundFraction = (whole: string, fraction: string, digits: number): [string, string] => {
	if (fraction.length <= digits) {
		return [whole, fraction];
	}
	const roundUp = Number(fraction[digits]) >= 5 ? 1n : 0n;
	const scaled = (BigInt(whole + fraction.slice(0, digits)) + roundUp)
		.toString()
		.padStart(digits + 1, '0');
	return [scaled.slice(0, scaled.length - digits), scaled.slice(scaled.length - digits)];
};

/**
 * A decimal string with thousands separators, formatted as text: never through a float. A computed figure rounds to
 * `maxFraction` digits; trailing zeros of the fraction go, then it is padded to `minFraction` digits.
 */
export const formatDecimalString = (
	value: string,
	minFraction = 0,
	maxFraction: number | null = null
): string => {
	const match = DECIMAL.exec(value.trim());
	if (!match) {
		return value;
	}
	const [, sign, printedWhole, printedFraction = ''] = match;
	const [whole, fraction] =
		maxFraction === null
			? [printedWhole, printedFraction]
			: roundFraction(printedWhole, printedFraction, maxFraction);
	const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	const trimmed = fraction.replace(/0+$/, '').padEnd(minFraction, '0');
	return `${sign}${grouped}${trimmed ? `.${trimmed}` : ''}`;
};

export const formatUnits = (units: string | null): string | null =>
	units === null ? null : formatDecimalString(units);

/** Converted and GBP figures are computed at full precision; they show in whole minor units. */
export const COMPUTED_AMOUNT_FRACTION = 2;

/** "GBP 1,250.00"; the code is left out when the line printed none. A computed amount rounds to `maxFraction`. */
export const formatAmount = (
	amount: string,
	currency: string | null | undefined,
	maxFraction: number | null = null
): string => {
	const formatted = formatDecimalString(amount, 2, maxFraction);
	return currency ? `${currency} ${formatted}` : formatted;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

/** A date-only value or an instant, as its calendar day ("15 Dec 2025"); anything else as given. */
export const formatIsoDate = (value: string): string =>
	ISO_DATE.test(value) ? formatBEDateToUiWithFallback(value, DATE_FORMATS.UI_SHORT) : value;

export const detailValue = (
	row: Pick<StagedTransactionRes, 'details'>,
	key: DealstackAiTransactionFieldKey
): string => row.details.find((field) => field.key === key)?.value ?? '';

const PEOPLE_FIELDS: readonly DealstackAiTransactionFieldKey[] = [
	'buyer',
	'seller',
	'boughtFor',
	'soldFor',
];

/** Buyer, seller, bought-for and sold-for as printed, each named once. */
export const peopleNames = (row: Pick<StagedTransactionRes, 'details'>): string[] => [
	...new Set(PEOPLE_FIELDS.map((key) => detailValue(row, key).trim()).filter(Boolean)),
];

export const securityName = (row: Pick<StagedTransactionRes, 'security' | 'details'>): string =>
	row.security?.name ?? detailValue(row, 'security');

/** Other documents' lines shown under this row. */
export const collapsedLineCount = (row: Pick<StagedTransactionRes, 'source'>): number =>
	row.source.duplicates.length;

/** The cost the row carries: the reviewer's entry in place of the printed figure, else what was printed. */
const shownCost = (costView: CostViewRes) => costView.entered ?? costView.original;

export const currencyCode = (costView: CostViewRes): string | null =>
	shownCost(costView)?.currency ?? costView.converted?.currency ?? null;

export const costSummary = (costView: CostViewRes): CostSummary => {
	const { converted, gbpAmount } = costView;
	const shown = shownCost(costView);
	const convertedDiffers = converted !== null && converted.currency !== shown?.currency;
	const gbpDiffers = shown?.currency !== 'GBP' && converted?.currency !== 'GBP';
	return {
		original: shown ? formatAmount(shown.amount, shown.currency) : null,
		converted: convertedDiffers
			? formatAmount(converted.amount, converted.currency, COMPUTED_AMOUNT_FRACTION)
			: null,
		gbp:
			gbpAmount !== null && gbpDiffers
				? formatAmount(gbpAmount, 'GBP', COMPUTED_AMOUNT_FRACTION)
				: null,
	};
};

/** Hover text for a cell that may truncate; the type cell's is its label, which the row supplies. */
export const cellTitle = (
	row: Pick<StagedTransactionRes, 'details' | 'evidence' | 'security' | 'issuerName'>,
	column: TransactionColumnKey
): string | undefined => {
	switch (column) {
		case 'issuer':
			return row.issuerName ?? undefined;
		case 'buyer':
			return detailValue(row, 'buyer');
		case 'seller':
			return detailValue(row, 'seller');
		case 'document':
			return row.evidence.fileName;
		case 'security':
			return securityName(row);
		default:
			return undefined;
	}
};
